import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { Calendar, Clock, AlertTriangle, CheckCircle, Car, Calculator, RotateCcw, Coffee, CalendarDays } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const STORAGE_KEY = 'driver_availability_data';

// Get the Monday of a given week
const getMonday = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  return new Date(d.setDate(diff));
};

// Get EU weeks (Monday 00:01 → Sunday 23:59)
const getEUWeeks = () => {
  const today = new Date();
  const currentMonday = getMonday(today);
  
  // Calculate 4 EU weeks (28 days ending this Sunday)
  const weeks = [];
  for (let w = 3; w >= 0; w--) {
    const weekStart = new Date(currentMonday);
    weekStart.setDate(currentMonday.getDate() - (w * 7));
    
    const weekDays = [];
    for (let d = 0; d < 7; d++) {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + d);
      weekDays.push({
        date: date.toISOString().split('T')[0],
        dayName: date.toLocaleDateString('ro-RO', { weekday: 'short' }),
        fullDayName: date.toLocaleDateString('ro-RO', { weekday: 'long' }),
        dayNum: date.getDate(),
        month: date.toLocaleDateString('ro-RO', { month: 'short' }),
        isToday: date.toDateString() === today.toDateString(),
        isFuture: date > today,
        weekday: date.getDay() // 0 = Sunday, 1 = Monday, etc.
      });
    }
    
    weeks.push({
      days: weekDays,
      weekNum: 4 - w,
      startDate: weekDays[0].date,
      endDate: weekDays[6].date,
      isCurrentWeek: w === 0,
      isPreviousWeek: w === 1
    });
  }
  
  return weeks;
};

export const AdminDriverCalculatorPage = () => {
  const [weeks, setWeeks] = useState([]);
  const [dayStates, setDayStates] = useState({});
  const [results, setResults] = useState(null);
  const [calculated, setCalculated] = useState(false);

  useEffect(() => {
    const euWeeks = getEUWeeks();
    setWeeks(euWeeks);
    
    // Load from localStorage
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setDayStates(parsed);
      } catch (e) {
        console.error('Failed to load saved data');
      }
    }
  }, []);

  // Save to localStorage whenever dayStates changes
  useEffect(() => {
    if (Object.keys(dayStates).length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dayStates));
    }
  }, [dayStates]);

  const toggleDay = (dateKey) => {
    setDayStates(prev => {
      const current = prev[dateKey] || { worked: false, startTime: '', endTime: '' };
      return {
        ...prev,
        [dateKey]: {
          ...current,
          worked: !current.worked,
          startTime: !current.worked ? '06:00' : '',
          endTime: !current.worked ? '15:00' : ''
        }
      };
    });
    setCalculated(false);
  };

  const updateTime = (dateKey, field, value) => {
    setDayStates(prev => ({
      ...prev,
      [dateKey]: {
        ...prev[dateKey],
        [field]: value
      }
    }));
    setCalculated(false);
  };

  const calculateHours = (startTime, endTime) => {
    if (!startTime || !endTime) return 0;
    const [startH, startM] = startTime.split(':').map(Number);
    const [endH, endM] = endTime.split(':').map(Number);
    let hours = endH - startH + (endM - startM) / 60;
    if (hours < 0) hours += 24; // overnight
    return Math.max(0, hours);
  };

  // Convert date string and time to timestamp
  const getTimestamp = (dateStr, timeStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const [hours, minutes] = (timeStr || '00:00').split(':').map(Number);
    return new Date(year, month - 1, day, hours, minutes).getTime();
  };

  // Get all work periods sorted by start time
  const getWorkPeriods = () => {
    const periods = [];
    const allDays = [...weeks[0].days, ...weeks[1].days, ...weeks[2].days, ...weeks[3].days];
    
    allDays.forEach(day => {
      const state = dayStates[day.date] || { worked: false };
      if (state.worked && !day.isFuture) {
        const startTime = state.startTime || '06:00';
        const endTime = state.endTime || '15:00';
        
        let startTs = getTimestamp(day.date, startTime);
        let endTs = getTimestamp(day.date, endTime);
        
        // If end time is before start time, it's overnight work
        if (endTs <= startTs) {
          endTs += 24 * 60 * 60 * 1000; // Add 24 hours
        }
        
        const hours = (endTs - startTs) / (1000 * 60 * 60);
        
        periods.push({
          date: day.date,
          dayNum: day.dayNum,
          month: day.month,
          startTs,
          endTs,
          startTime,
          endTime,
          hours
        });
      }
    });
    
    return periods.sort((a, b) => a.startTs - b.startTs);
  };

  // Find the last weekly rest (≥24h continuous break)
  const findLastWeeklyRest = (periods) => {
    if (periods.length === 0) return null;
    
    let lastRestEnd = null;
    
    for (let i = 1; i < periods.length; i++) {
      const prevEnd = periods[i - 1].endTs;
      const currStart = periods[i].startTs;
      const breakHours = (currStart - prevEnd) / (1000 * 60 * 60);
      
      // If break is ≥24h, this is a weekly rest
      if (breakHours >= 24) {
        lastRestEnd = currStart; // The moment rest ends = next work starts
      }
    }
    
    return lastRestEnd;
  };

  // Count activity windows (24h periods with any work) since last rest
  const countActivityWindows = (periods, lastRestEnd) => {
    if (periods.length === 0) return 0;
    
    // Filter periods after last rest
    const periodsAfterRest = lastRestEnd 
      ? periods.filter(p => p.startTs >= lastRestEnd)
      : periods;
    
    if (periodsAfterRest.length === 0) return 0;
    
    // Start counting from the first work after rest
    const firstWorkStart = periodsAfterRest[0].startTs;
    const now = Date.now();
    
    // Count 24h windows with activity
    let windowsWithActivity = 0;
    let windowStart = firstWorkStart;
    
    while (windowStart < now) {
      const windowEnd = windowStart + 24 * 60 * 60 * 1000;
      
      // Check if any work period overlaps with this window
      const hasActivity = periodsAfterRest.some(p => 
        (p.startTs < windowEnd && p.endTs > windowStart)
      );
      
      if (hasActivity) {
        windowsWithActivity++;
      }
      
      windowStart = windowEnd;
    }
    
    return windowsWithActivity;
  };

  const calculate = () => {
    if (weeks.length < 4) return;
    
    const periods = getWorkPeriods();
    const infringements = [];
    const warnings = [];
    
    // === RULE 1: Calculate hours for EU weeks (56h/week, 90h/2 weeks) ===
    const previousWeek = weeks[2];
    const currentWeek = weeks[3];
    
    let previousWeekHours = 0;
    let currentWeekHours = 0;
    let daysOver9hPreviousWeek = 0;
    let daysOver9hCurrentWeek = 0;
    let workDaysPreviousWeek = 0;
    let workDaysCurrentWeek = 0;
    const dailyHours = {};
    
    // Calculate previous week hours (Săpt. 3)
    previousWeek.days.forEach(day => {
      const state = dayStates[day.date] || { worked: false };
      if (state.worked) {
        workDaysPreviousWeek++;
        let hours = state.startTime && state.endTime 
          ? calculateHours(state.startTime, state.endTime) 
          : 9;
        dailyHours[day.date] = hours;
        previousWeekHours += hours;
        
        if (hours > 10) {
          infringements.push(`${day.dayNum} ${day.month}: ${hours.toFixed(1)}h depășește limita zilnică de 10h`);
        } else if (hours > 9) {
          daysOver9hPreviousWeek++;
        }
      }
    });
    
    // Calculate current week hours (Săpt. 4)
    currentWeek.days.forEach(day => {
      const state = dayStates[day.date] || { worked: false };
      if (state.worked && !day.isFuture) {
        workDaysCurrentWeek++;
        let hours = state.startTime && state.endTime 
          ? calculateHours(state.startTime, state.endTime) 
          : 9;
        dailyHours[day.date] = hours;
        currentWeekHours += hours;
        
        if (hours > 10) {
          infringements.push(`${day.dayNum} ${day.month}: ${hours.toFixed(1)}h depășește limita zilnică de 10h`);
        } else if (hours > 9) {
          daysOver9hCurrentWeek++;
        }
      }
    });
    
    // Check 10h days limit (max 2 per week)
    if (daysOver9hPreviousWeek > 2) {
      infringements.push(`Săpt. precedentă: ${daysOver9hPreviousWeek} zile cu >9h (max 2 permise)`);
    }
    if (daysOver9hCurrentWeek > 2) {
      infringements.push(`Săpt. curentă: ${daysOver9hCurrentWeek} zile cu >9h (max 2 permise)`);
    }
    
    // Check 7 days worked (no rest day in week)
    if (workDaysPreviousWeek === 7) {
      warnings.push('Săpt. precedentă: 7 zile lucrate - lipsă odihnă săptămânală!');
    }
    if (workDaysCurrentWeek === 7) {
      warnings.push('Săpt. curentă: 7 zile lucrate - lipsă odihnă săptămânală!');
    }
    
    const total2Weeks = previousWeekHours + currentWeekHours;
    const remainingWeekly = Math.max(0, 56 - currentWeekHours);
    const remaining2Weeks = Math.max(0, 90 - total2Weeks);
    
    // === RULE 2: 6-day rule with 24h windows ===
    const lastRestEnd = findLastWeeklyRest(periods);
    const activityWindows = countActivityWindows(periods, lastRestEnd);
    
    // Days remaining by 6-day rule
    const daysRemainingBy6DayRule = Math.max(0, 6 - activityWindows);
    
    // === RULE 3: Calculate days remaining by 90h rule (estimate) ===
    // Don't divide by 9 directly - consider actual work pattern
    // Estimate: if average daily hours, how many more days?
    let avgDailyHours = 9; // Default assumption
    if (periods.length > 0) {
      const totalHoursWorked = periods.reduce((sum, p) => sum + p.hours, 0);
      avgDailyHours = Math.max(8, Math.min(10, totalHoursWorked / periods.length));
    }
    
    // Remaining hours from 90h limit
    const daysRemainingBy90hRule = remaining2Weeks > 0 
      ? Math.floor(remaining2Weeks / avgDailyHours)
      : 0;
    
    // === FINAL: Use minimum of both rules ===
    const daysRemaining = Math.min(daysRemainingBy6DayRule, daysRemainingBy90hRule);
    
    // Max hours available (minimum of weekly and biweekly limits)
    const maxHoursAvailable = Math.min(remainingWeekly, remaining2Weeks);
    
    // Determine if rest is needed
    const needsRestNow = activityWindows >= 6;
    const needsRestSoon = activityWindows >= 5;
    
    // Weekly/biweekly exceeded
    const weeklyExceeded = currentWeekHours > 56;
    const biweeklyExceeded = total2Weeks > 90;
    
    if (weeklyExceeded) {
      infringements.push(`Săpt. curentă: ${currentWeekHours.toFixed(1)}h depășește limita de 56h`);
    }
    if (biweeklyExceeded) {
      infringements.push(`Total 2 săpt.: ${total2Weeks.toFixed(1)}h depășește limita de 90h`);
    }
    
    // Format last rest info
    let lastRestInfo = null;
    if (lastRestEnd) {
      const lastRestDate = new Date(lastRestEnd);
      lastRestInfo = lastRestDate.toLocaleDateString('ro-RO', { 
        day: 'numeric', 
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    }

    setResults({
      previousWeekHours: previousWeekHours.toFixed(1),
      currentWeekHours: currentWeekHours.toFixed(1),
      total2Weeks: total2Weeks.toFixed(1),
      remainingWeekly: remainingWeekly.toFixed(1),
      remaining2Weeks: remaining2Weeks.toFixed(1),
      maxHoursAvailable: maxHoursAvailable.toFixed(1),
      
      // 6-day rule results
      activityWindows,
      daysRemainingBy6DayRule,
      lastRestInfo,
      
      // 90h rule results  
      daysRemainingBy90hRule,
      avgDailyHours: avgDailyHours.toFixed(1),
      
      // Final result
      daysRemaining,
      totalDaysWorked: periods.length,
      
      // Status flags
      weeklyExceeded,
      biweeklyExceeded,
      hasWarning: weeklyExceeded || biweeklyExceeded || needsRestNow || infringements.length > 0,
      needsRestSoon,
      needsRestNow,
      
      // Additional info
      infringements,
      warnings,
      daysOver9hCurrentWeek,
      daysOver9hPreviousWeek,
      workDaysPreviousWeek,
      workDaysCurrentWeek,
      dailyHours
    });
    setCalculated(true);
    toast.success('Calcul finalizat!');
  };

  const resetAll = () => {
    if (window.confirm('Sigur doriți să resetați toate datele?')) {
      setDayStates({});
      setResults(null);
      setCalculated(false);
      localStorage.removeItem(STORAGE_KEY);
      toast.success('Date resetate');
    }
  };

  // Group days by week - now using EU weeks
  const weekLabels = [
    'Săpt. 1 (veche)',
    'Săpt. 2',
    'Săpt. 3 (precedentă)',
    'Săpt. 4 (curentă)'
  ];

  return (
    <AdminLayout>
      <div data-testid="admin-driver-calculator-page" className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-500/20 rounded-xl">
              <Car className="text-blue-400" size={32} />
            </div>
            <div>
              <h1 className="text-3xl font-serif">Calculator Disponibilitate Șofer UE</h1>
              <p className="text-muted-foreground text-sm">Regulamentul (CE) 561/2006</p>
            </div>
          </div>
          <Button onClick={resetAll} variant="outline" size="sm">
            <RotateCcw size={16} className="mr-2" />
            Resetează
          </Button>
        </div>

        {/* Info Box */}
        <div className="glass p-4 mb-6 border border-blue-500/30 bg-blue-500/5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-blue-400 flex-shrink-0 mt-1" size={20} />
            <div className="text-sm">
              <p className="font-medium text-blue-400 mb-1">Limite conform Regulamentului (CE) 561/2006:</p>
              <ul className="text-muted-foreground space-y-1">
                <li>• Maximum <strong className="text-white">56 ore</strong> conducere pe săptămână</li>
                <li>• Maximum <strong className="text-white">90 ore</strong> conducere în 2 săptămâni consecutive</li>
                <li>• Maximum <strong className="text-white">9 ore</strong> conducere pe zi (poate fi extins la 10 ore de 2 ori/săptămână)</li>
                <li>• După <strong className="text-white">6 zile</strong> consecutive de lucru → pauză obligatorie de min. 24h</li>
              </ul>
              <p className="mt-2 text-xs text-cyan-400">
                <CalendarDays size={14} className="inline mr-1" />
                Săptămâna UE: Luni 00:01 → Duminică 23:59
              </p>
            </div>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="glass p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Calendar size={20} className="text-primary" />
            Calendar Ultimele 4 Săptămâni UE
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            Click pe zi pentru a marca: <span className="text-green-400">Verde = Lucrat</span> | <span className="text-red-400">Roșu = Liber</span>
          </p>

          {/* Day headers */}
          <div className="grid grid-cols-7 gap-2 mb-2">
            {['Lun', 'Mar', 'Mie', 'Joi', 'Vin', 'Sâm', 'Dum'].map((day, i) => (
              <div key={day} className={`text-center text-xs font-medium py-1 ${i >= 5 ? 'text-amber-400' : 'text-muted-foreground'}`}>
                {day}
              </div>
            ))}
          </div>

          <div className="space-y-6">
            {weeks.map((week, weekIdx) => (
              <div key={weekIdx} className={`p-4 rounded-lg ${week.isCurrentWeek || week.isPreviousWeek ? 'bg-slate-800/50 border border-primary/20' : 'bg-slate-900/30'}`}>
                <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                  {weekLabels[weekIdx]}
                  <span className="text-xs text-muted-foreground">
                    ({week.days[0].dayNum} {week.days[0].month} - {week.days[6].dayNum} {week.days[6].month})
                  </span>
                  {(week.isCurrentWeek || week.isPreviousWeek) && (
                    <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded">Se calculează</span>
                  )}
                </h3>
                <div className="grid grid-cols-7 gap-2">
                  {week.days.map((day) => {
                    const state = dayStates[day.date] || { worked: false, startTime: '', endTime: '' };
                    const isWorked = state.worked;
                    const isWeekend = day.weekday === 0 || day.weekday === 6;
                    
                    return (
                      <div key={day.date} className="flex flex-col">
                        <button
                          onClick={() => !day.isFuture && toggleDay(day.date)}
                          disabled={day.isFuture}
                          className={`
                            p-2 rounded-lg text-center transition-all border-2
                            ${day.isFuture 
                              ? 'bg-slate-900/50 border-slate-700 text-slate-600 cursor-not-allowed opacity-50'
                              : isWorked 
                                ? 'bg-green-500/20 border-green-500 text-green-400' 
                                : 'bg-red-500/10 border-red-500/30 text-red-400'
                            }
                            ${day.isToday ? 'ring-2 ring-primary ring-offset-2 ring-offset-slate-900' : ''}
                            ${isWeekend && !day.isFuture ? 'bg-opacity-50' : ''}
                            ${!day.isFuture ? 'hover:scale-105' : ''}
                          `}
                        >
                          <div className={`text-xs ${isWeekend ? 'text-amber-400' : 'opacity-70'}`}>{day.dayName}</div>
                          <div className="text-lg font-bold">{day.dayNum}</div>
                          <div className="text-xs opacity-70">{day.month}</div>
                          <div className="mt-1">
                            {day.isFuture ? (
                              <span className="text-xs">-</span>
                            ) : isWorked ? (
                              <CheckCircle size={16} className="mx-auto" />
                            ) : (
                              <span className="text-lg">✗</span>
                            )}
                          </div>
                        </button>
                        
                        {isWorked && !day.isFuture && (
                          <div className="mt-2 space-y-1">
                            <input
                              type="time"
                              value={state.startTime || ''}
                              onChange={(e) => updateTime(day.date, 'startTime', e.target.value)}
                              className="w-full px-1 py-1 text-xs bg-slate-800 border border-green-500/30 rounded text-center"
                              placeholder="Start"
                            />
                            <input
                              type="time"
                              value={state.endTime || ''}
                              onChange={(e) => updateTime(day.date, 'endTime', e.target.value)}
                              className="w-full px-1 py-1 text-xs bg-slate-800 border border-green-500/30 rounded text-center"
                              placeholder="Stop"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Calculate Button */}
        <div className="text-center mb-6">
          <Button onClick={calculate} size="lg" className="px-12 py-6 text-lg bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700">
            <Calculator size={24} className="mr-3" />
            Calculează Disponibil
          </Button>
        </div>

        {/* Results Panel */}
        {calculated && results && (
          <div className={`glass p-6 ${results.hasWarning ? 'border-2 border-red-500 bg-red-500/5' : 'border-2 border-green-500 bg-green-500/5'}`}>
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              {results.hasWarning ? (
                <>
                  <AlertTriangle className="text-red-400" size={24} />
                  <span className="text-red-400">Atenție! Verificați limitele!</span>
                </>
              ) : (
                <>
                  <CheckCircle className="text-green-400" size={24} />
                  <span className="text-green-400">Rezultate Calcul</span>
                </>
              )}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {/* Previous Week Hours */}
              <div className="bg-slate-800/50 p-4 rounded-lg">
                <div className="text-sm text-muted-foreground mb-1">Ore săptămâna trecută</div>
                <div className="text-3xl font-bold text-cyan-400">{results.previousWeekHours}h</div>
              </div>

              {/* Current Week Hours */}
              <div className={`p-4 rounded-lg ${results.weeklyExceeded ? 'bg-red-500/20 border border-red-500' : 'bg-slate-800/50'}`}>
                <div className="text-sm text-muted-foreground mb-1">Ore săptămâna curentă</div>
                <div className={`text-3xl font-bold ${results.weeklyExceeded ? 'text-red-400' : 'text-blue-400'}`}>
                  {results.currentWeekHours}h
                  <span className="text-lg text-muted-foreground"> / 56h</span>
                </div>
                {results.weeklyExceeded && (
                  <div className="text-xs text-red-400 mt-1">⚠️ DEPĂȘIT LIMITA SĂPTĂMÂNALĂ!</div>
                )}
              </div>

              {/* 2 Weeks Total */}
              <div className={`p-4 rounded-lg ${results.biweeklyExceeded ? 'bg-red-500/20 border border-red-500' : 'bg-slate-800/50'}`}>
                <div className="text-sm text-muted-foreground mb-1">Total 2 săptămâni</div>
                <div className={`text-3xl font-bold ${results.biweeklyExceeded ? 'text-red-400' : 'text-purple-400'}`}>
                  {results.total2Weeks}h
                  <span className="text-lg text-muted-foreground"> / 90h</span>
                </div>
                {results.biweeklyExceeded && (
                  <div className="text-xs text-red-400 mt-1">⚠️ DEPĂȘIT LIMITA 2 SĂPTĂMÂNI!</div>
                )}
              </div>
            </div>

            {/* Rest/Break Information */}
            <div className={`p-4 rounded-lg mb-6 ${results.needsRestNow ? 'bg-red-500/20 border-2 border-red-500' : results.needsRestSoon ? 'bg-amber-500/20 border border-amber-500' : 'bg-slate-800/50'}`}>
              <div className="flex items-center gap-3 mb-2">
                <Coffee className={`${results.needsRestNow ? 'text-red-400' : results.needsRestSoon ? 'text-amber-400' : 'text-green-400'}`} size={24} />
                <div className="text-lg font-medium">
                  {results.needsRestNow ? (
                    <span className="text-red-400">⚠️ PAUZĂ OBLIGATORIE ACUM!</span>
                  ) : results.needsRestSoon ? (
                    <span className="text-amber-400">⚡ Pauză necesară în curând</span>
                  ) : (
                    <span className="text-green-400">✓ Statusul pauzei este OK</span>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Zile consecutive lucrate: </span>
                  <span className={`font-bold ${results.consecutiveWorkDays >= 6 ? 'text-red-400' : results.consecutiveWorkDays >= 5 ? 'text-amber-400' : 'text-white'}`}>
                    {results.consecutiveWorkDays} / 6
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Zile până la pauza obligatorie: </span>
                  <span className={`font-bold ${results.daysUntilMandatoryRest === 0 ? 'text-red-400' : results.daysUntilMandatoryRest === 1 ? 'text-amber-400' : 'text-green-400'}`}>
                    {results.daysUntilMandatoryRest === 0 ? 'ACUM' : `${results.daysUntilMandatoryRest} zile`}
                  </span>
                </div>
              </div>
              {results.needsRestNow && (
                <p className="text-red-400 text-sm mt-2">
                  Conform CE 561/2006, după 6 zile consecutive de lucru trebuie să iei o pauză de minimum 24 de ore!
                </p>
              )}
            </div>

            {/* Infringements List */}
            {results.infringements && results.infringements.length > 0 && (
              <div className="p-4 rounded-lg mb-6 bg-red-500/20 border-2 border-red-500">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="text-red-400" size={20} />
                  <span className="font-bold text-red-400">INFRINGEMENTS DETECTATE:</span>
                </div>
                <ul className="list-disc list-inside text-sm text-red-300 space-y-1">
                  {results.infringements.map((inf, idx) => (
                    <li key={idx}>{inf}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Warnings List */}
            {results.warnings && results.warnings.length > 0 && (
              <div className="p-4 rounded-lg mb-6 bg-amber-500/20 border border-amber-500">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="text-amber-400" size={20} />
                  <span className="font-bold text-amber-400">AVERTISMENTE:</span>
                </div>
                <ul className="list-disc list-inside text-sm text-amber-300 space-y-1">
                  {results.warnings.map((warn, idx) => (
                    <li key={idx}>{warn}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* 10h Days Usage */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className={`p-4 rounded-lg ${results.daysOver9hPreviousWeek > 2 ? 'bg-red-500/20 border border-red-500' : 'bg-slate-800/50'}`}>
                <div className="text-sm text-muted-foreground mb-1">Zile cu 10h (săpt. trecută)</div>
                <div className={`text-2xl font-bold ${results.daysOver9hPreviousWeek > 2 ? 'text-red-400' : 'text-cyan-400'}`}>
                  {results.daysOver9hPreviousWeek} / 2
                </div>
                {results.daysOver9hPreviousWeek > 2 && (
                  <div className="text-xs text-red-400">⚠️ Max 2 zile cu 10h/săptămână!</div>
                )}
              </div>
              <div className={`p-4 rounded-lg ${results.daysOver9hCurrentWeek > 2 ? 'bg-red-500/20 border border-red-500' : 'bg-slate-800/50'}`}>
                <div className="text-sm text-muted-foreground mb-1">Zile cu 10h (săpt. curentă)</div>
                <div className={`text-2xl font-bold ${results.daysOver9hCurrentWeek > 2 ? 'text-red-400' : 'text-blue-400'}`}>
                  {results.daysOver9hCurrentWeek} / 2
                </div>
                {results.daysOver9hCurrentWeek > 2 && (
                  <div className="text-xs text-red-400">⚠️ Max 2 zile cu 10h/săptămână!</div>
                )}
              </div>
            </div>

            {/* Remaining */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-green-500/10 border border-green-500/30 p-4 rounded-lg">
                <div className="text-sm text-green-400 mb-1">Ore rămase săptămâna aceasta</div>
                <div className="text-4xl font-bold text-green-400">{results.remainingWeekly}h</div>
              </div>
              <div className="bg-green-500/10 border border-green-500/30 p-4 rounded-lg">
                <div className="text-sm text-green-400 mb-1">Ore rămase din limita 2 săptămâni</div>
                <div className="text-4xl font-bold text-green-400">{results.remaining2Weeks}h</div>
              </div>
            </div>

            {/* Summary */}
            <div className={`p-6 rounded-lg text-center ${results.hasWarning ? 'bg-red-500/20' : 'bg-gradient-to-r from-green-500/20 to-cyan-500/20'}`}>
              {results.hasWarning ? (
                <div>
                  <AlertTriangle className="mx-auto mb-2 text-red-400" size={40} />
                  <p className="text-xl font-bold text-red-400">ATENȚIE: LIMITE DEPĂȘITE SAU PAUZĂ NECESARĂ!</p>
                  <p className="text-muted-foreground">Verificați orele de conducere și respectați perioadele de odihnă.</p>
                  {results.needsRestNow && (
                    <p className="text-red-400 font-bold mt-2">Trebuie să iei pauză de min. 24h înainte de a continua!</p>
                  )}
                </div>
              ) : (
                <div>
                  <CheckCircle className="mx-auto mb-2 text-green-400" size={40} />
                  <p className="text-2xl font-bold text-green-400 mb-2">
                    Mai poți lucra {results.daysRemaining} zile până la limită
                  </p>
                  <p className="text-xl text-cyan-400">
                    Mai poți conduce maxim <strong>{results.maxHoursAvailable}h</strong>
                  </p>
                  {results.daysUntilMandatoryRest > 0 && results.daysUntilMandatoryRest < 3 && (
                    <p className="text-amber-400 mt-2">
                      ⚡ Atenție: Mai ai {results.daysUntilMandatoryRest} zile până la pauza obligatorie!
                    </p>
                  )}
                  <p className="text-sm text-muted-foreground mt-2">
                    fără a depăși limitele Regulamentului (CE) 561/2006
                  </p>
                </div>
              )}
            </div>

            {/* Stats */}
            <div className="mt-4 text-center text-sm text-muted-foreground">
              Total zile lucrate în ultimele 28 zile: <strong className="text-white">{results.totalDaysWorked}</strong>
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="mt-6 glass p-4">
          <h3 className="text-sm font-medium mb-2">Legendă:</h3>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-500/20 border-2 border-green-500 rounded"></div>
              <span>Zi lucrată</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-500/10 border-2 border-red-500/30 rounded"></div>
              <span>Zi liberă</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 ring-2 ring-primary rounded"></div>
              <span>Ziua de azi</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="px-2 py-0.5 bg-primary/20 text-primary text-xs rounded">Se calculează</div>
              <span>Săptămâni incluse în calcul</span>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDriverCalculatorPage;
